import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { query } from 'lit/decorators.js';
import { emit } from '../../internal/event.js';
import { getTextContent } from '../../internal/slot.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
import styles from './arc-menu.styles.js';
export default class ArcMenu extends LitElement {
    constructor() {
        super(...arguments);
        this.typeToSelectString = '';
    }
    getAllItems(options = { includeDisabled: true }) {
        const { includeDisabled } = options;
        return [...this.defaultSlot.assignedElements({ flatten: true })].filter((el) => {
            if (el.getAttribute('role') !== 'menuitem') {
                return false;
            }
            return !(!includeDisabled && el.disabled);
        });
    }
    getCurrentItem() {
        return this.getAllItems({ includeDisabled: false }).find(i => i.getAttribute('tabindex') === '0');
    }
    setCurrentItem(item) {
        const items = this.getAllItems({ includeDisabled: false });
        const activeItem = item.disabled ? items[0] : item;
        items.forEach(i => i.setAttribute('tabindex', i === activeItem ? '0' : '-1'));
    }
    typeToSelect(key) {
        const items = this.getAllItems({ includeDisabled: false });
        clearTimeout(this.typeToSelectTimeout);
        this.typeToSelectTimeout = window.setTimeout(() => {
            this.typeToSelectString = '';
        }, 750);
        this.typeToSelectString += key.toLowerCase();
        for (const item of items) {
            const slot = item.shadowRoot.querySelector('slot:not([name])');
            const label = getTextContent(slot).toLowerCase().trim();
            if (label.substring(0, this.typeToSelectString.length) === this.typeToSelectString) {
                this.setCurrentItem(item);
                item.focus();
                break;
            }
        }
    }
    _handleClick(event) {
        const target = event.target;
        const item = target.closest('arc-menu-item');
        if (item && !item.disabled) {
            emit(this, ARC_EVENTS.select, { detail: { item } });
        }
    }
    handleKeyDown(event) {
        if (event.key === 'Enter') {
            const item = this.getCurrentItem();
            event.preventDefault();
            if (item) {
                item.click();
            }
        }
        if (event.key === ' ') {
            event.preventDefault();
        }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            const items = this.getAllItems({ includeDisabled: false });
            const activeItem = this.getCurrentItem();
            let index = activeItem ? items.indexOf(activeItem) : 0;
            if (items.length) {
                event.preventDefault();
                if (event.key === 'ArrowDown') {
                    index++;
                }
                else if (event.key === 'ArrowUp') {
                    index--;
                }
                else if (event.key === 'Home') {
                    index = 0;
                }
                else if (event.key === 'End') {
                    index = items.length - 1;
                }
                if (index < 0)
                    index = 0;
                if (index > items.length - 1)
                    index = items.length - 1;
                this.setCurrentItem(items[index]);
                items[index].focus();
            }
        }
        this.typeToSelect(event.key);
    }
    _handleSlotChange() {
        const items = this.getAllItems({ includeDisabled: false });
        if (items.length) {
            this.setCurrentItem(items[0]);
        }
    }
    render() {
        return html `
      <div id="main" role="menu" @click=${this._handleClick} @keydown=${this.handleKeyDown}>
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
    }
}
ArcMenu.tag = 'arc-menu';
ArcMenu.styles = styles;
__decorate([
    query('#menu')
], ArcMenu.prototype, "menu", void 0);
__decorate([
    query('slot')
], ArcMenu.prototype, "defaultSlot", void 0);
